<template>
	<div
		class="expandable-container"
		:class="{
			'is-active': expanded,
			'expandable-container--overflow' : transitioning || !expanded,
			'hide': !expanded && !transitioning
		}"
		:style="`max-height: ${height};`"
		:aria-hidden="!expanded"
		@transitionstart="transitionStart"
		@transitionend="transitionEnd"
	>
		<div ref="content" :aria-hidden="!expanded">
			<slot v-if="alwaysRender || expanded || transitioning" />
		</div>
	</div>
</template>

<script lang="ts">
	import { defineComponent } from 'vue';
	export default defineComponent({
		name: 'CExpandable',
		props: {
			expanded: {
				type: Boolean,
				required: true,
				default: false
			},
			alwaysRender: { type: Boolean, default: false }
		},
		emits: ['expanded'],
		data() {
			return {
				height: '',
				transitioning: false
			};
		},
		watch: {
			expanded() {
				this.transitioning = true;
				this.determineHeight();
			}
		},
		async mounted() {
			await this.$nextTick();
			if (!this.expanded)
				this.height = 0;
		},
		methods: {
			determineHeight() {
				// If going to an expanded state, set max-height from nothing to zero so it can transition
				// to the content's height. If going to a contracted state, the reverse should happen.
				if (this.expanded) {
					this.height = 0;
					requestAnimationFrame(() => {
						const contentHeight = this.$refs.content.clientHeight;
						// Skip animation when content is hidden (no transitionend will fire)
						if (contentHeight === 0) {
							this.height = '';
							this.transitioning = false;
							this.$emit('expanded', contentHeight);
							return;
						}
						this.height = contentHeight + 'px';
						this.$emit('expanded', contentHeight);
					});
				}
				else {
					const contentHeight = this.$refs.content.clientHeight;
					// Skip animation when content is hidden (no transitionend will fire)
					if (contentHeight === 0) {
						this.height = 0;
						this.transitioning = false;
						return;
					}
					this.height = contentHeight + 'px';
					requestAnimationFrame(() => {
						this.height = 0;
					});
				}
			},
			transitionEnd(e) {
				if (e.propertyName === 'max-height' && e.srcElement === this.$el) {
					this.transitioning = false;
					if (this.expanded)
						this.height = '';
				}
			},
			transitionStart(e) {
				if (e.propertyName === 'max-height' && e.srcElement === this.$el)
					this.transitioning = true;
			}
		}
	});
</script>

<style lang="scss">
	.expandable-container {
		transition: max-height .3s;

		&--overflow {
			overflow-y: hidden;
		}
	}
</style>